/**
 * MIDBOSS — TWO VEHICLES.
 * Spec: bible 04-BOSSES §1.
 *
 * Two captured Guard heavies, taken at the same depot on the same day. They are
 * NOT symmetrical, and that is the entire encounter design:
 *
 *   LEFT  carries a long-barrel autocannon and fires AIMED. It punishes you for
 *         standing still. Kill it if you would rather move than read.
 *   RIGHT carries a boxed missile cell and fires STATIC spreads. It fills space.
 *         Kill it if you would rather read than move.
 *
 * So the player chooses which pressure they personally find worse — a real
 * decision rather than a coin flip. And whichever survives ESCALATES: it takes
 * over the dead one's role, so killing one never simply halves the difficulty.
 *
 * It is deliberately less theatrical than BATTERY NINE. A midboss should feel
 * like a hard fight; the stage boss should feel like an event.
 */

import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import type { BulletSystem } from './BulletSystem'
import { emit, type PatternSpec } from './PatternGrammar'
import { getDifficulty } from '../game/difficulty'

type Half = 'left' | 'right'

interface Vehicle {
  half: Half
  hullHp: number
  turretHp: number
  hullAlive: boolean
  turretAlive: boolean
  /** Local X offset of this vehicle from the pair's centre. */
  originX: number
  hullNode: THREE.Object3D | null
  turretNode: THREE.Object3D | null
}

/** Base attack per half. Enraged variants fire faster and wider. */
const ATTACKS: Record<Half, PatternSpec> = {
  // Aimed triple — forces movement.
  left: {
    kind: 'aimed_burst', count: 3, speed: 17, spreadDeg: 9, aimed: true,
    cadenceHz: 0.72, telegraphMs: 350, bullet: 'bolt',
  },
  // Static seven-fan with an authored hole — tells you where to stand.
  right: {
    kind: 'fan', count: 7, speed: 11, spreadDeg: 58, gapIndex: 3, gapWidth: 1,
    cadenceHz: 0.6, telegraphMs: 520, bullet: 'pellet',
  },
}

const TOTAL_HP = 5400

export class MidbossSystem {
  readonly group = new THREE.Group()

  private vehicles: Vehicle[] = [
    { half: 'left', hullHp: 1600, turretHp: 1100, hullAlive: true, turretAlive: true, originX: -4.0, hullNode: null, turretNode: null },
    { half: 'right', hullHp: 1600, turretHp: 1100, hullAlive: true, turretAlive: true, originX: 4.0, hullNode: null, turretNode: null },
  ]

  private model: THREE.Object3D | null = null
  active = false
  defeated = false
  private x = 0
  private y = 16
  private age = 0
  private fireTimer = 0
  private deathTimer = -1
  private readonly SCALE = 1.35

  onEvent: ((kind: 'part' | 'enrage' | 'death', x: number, y: number) => void) | null = null

  async load(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync('/models/thunderhead/midboss-two-vehicles.glb')
      this.model = gltf.scene
      this.model.rotation.x = Math.PI / 2
      this.model.scale.setScalar(this.SCALE)
      this.vehicles[0].hullNode = this.model.getObjectByName('tv_left_hull') ?? null
      this.vehicles[0].turretNode = this.model.getObjectByName('tv_left_turret') ?? null
      this.vehicles[1].hullNode = this.model.getObjectByName('tv_right_hull') ?? null
      this.vehicles[1].turretNode = this.model.getObjectByName('tv_right_turret') ?? null
      this.group.add(this.model)
      // load() is async and may resolve after spawn() — respect current state.
      this.group.visible = this.active
    } catch (err) {
      console.warn('TWO VEHICLES model unavailable', err)
    }
  }

  get healthFraction(): number {
    const rem = this.vehicles.reduce(
      (s, v) => s + Math.max(0, v.hullHp) + Math.max(0, v.turretHp), 0,
    )
    return rem / TOTAL_HP
  }

  /** Per-component readout for the HUD boss panel. */
  componentStatus(): { id: string; frac: number; alive: boolean }[] {
    const out: { id: string; frac: number; alive: boolean }[] = []
    for (const v of this.vehicles) {
      out.push({ id: `${v.half}_turret`, frac: Math.max(0, v.turretHp) / 1100, alive: v.turretAlive })
      out.push({ id: `${v.half}_hull`, frac: Math.max(0, v.hullHp) / 1600, alive: v.hullAlive })
    }
    return out
  }

  /** A half is enraged once its partner is gone — it takes over both roles. */
  private enraged(v: Vehicle): boolean {
    const other = this.vehicles.find((o) => o !== v)
    return !!other && !other.hullAlive
  }

  spawn(): void {
    this.active = true
    this.defeated = false
    this.age = 0
    this.deathTimer = -1
    this.y = 16
    this.group.visible = true
  }

  update(dt: number, playerX: number, playerY: number, bullets: BulletSystem): void {
    if (!this.active) return
    this.age += dt

    if (this.defeated) {
      this.deathTimer += dt
      if (this.model) {
        this.model.rotation.z = Math.min(0.5, this.deathTimer * 0.3)
        this.group.position.y = this.y - Math.min(3, this.deathTimer * 1.2)
      }
      if (this.deathTimer > 2.6) {
        this.active = false
        this.group.visible = false
      }
      return
    }

    // Entry: roll in from the top, then hold a slow lateral patrol. Ground
    // vehicles do not hover — they advance and stop.
    if (this.age < 2.5) {
      this.y = 16 - (this.age / 2.5) * 5
    } else {
      this.x = Math.sin((this.age - 2.5) * 0.34) * 3.6
    }
    this.group.position.set(this.x, this.y, 0)

    // Turrets track the player. This is the tell that they are still crewed
    // by something — and it is what makes the aimed half feel dangerous.
    for (const v of this.vehicles) {
      if (!v.turretAlive || !v.turretNode) continue
      const wx = this.x + v.originX * this.SCALE
      const angle = Math.atan2(playerX - wx, this.y - playerY)
      v.turretNode.rotation.y += (angle - v.turretNode.rotation.y) * Math.min(1, dt * 2.4)
    }

    this.fireTimer -= dt
    if (this.age > 2.5 && this.fireTimer <= 0) this.fire(playerX, playerY, bullets)
  }

  private fire(playerX: number, playerY: number, bullets: BulletSystem): void {
    let fired = false
    let nextDelay = 1.4

    for (const v of this.vehicles) {
      if (!v.turretAlive) continue
      const rage = this.enraged(v)
      const base = ATTACKS[v.half]
      // An enraged survivor also fires its dead partner's pattern, so removing
      // one vehicle changes the SHAPE of the fight rather than halving it.
      const specs: PatternSpec[] = rage
        ? [base, ATTACKS[v.half === 'left' ? 'right' : 'left']]
        : [base]

      for (const spec of specs) {
        const diff = getDifficulty()
        const raged = rage
          ? { ...spec, count: spec.count + 2, cadenceHz: spec.cadenceHz * 1.35 }
          : spec
        const resolved = {
          ...raged,
          count: Math.max(1, Math.round(raged.count * diff.bulletScale)),
          speed: raged.speed * diff.speedScale,
        }
        const ox = this.x + v.originX * this.SCALE
        const shots = emit(resolved, ox, this.y - 1.6, playerX, playerY, this.age * 1.1)
        for (const b of shots) bullets.spawn(b.x, b.y, b.vx, b.vy, b.radius * 0.55, 9)
        fired = true
      }
      nextDelay = Math.min(nextDelay, (1 / (base.cadenceHz * (rage ? 1.35 : 1))) * getDifficulty().cadenceScale)
    }
    this.fireTimer = fired ? nextDelay : 1.4
  }

  /**
   * Damage resolution. Turrets are the exposed target; the hull only takes
   * damage once its turret is gone, so the player is taught to strip the guns
   * before killing the vehicle.
   */
  damage(worldX: number, amount: number): { hit: boolean; destroyed: boolean } {
    if (!this.active || this.defeated) return { hit: false, destroyed: false }

    const lx = (worldX - this.x) / this.SCALE
    const v = lx < 0 ? this.vehicles[0] : this.vehicles[1]
    if (!v.hullAlive) {
      // That half is gone — spill damage onto the survivor.
      const other = this.vehicles.find((o) => o.hullAlive)
      if (!other) return { hit: false, destroyed: false }
      return this.applyTo(other, amount)
    }
    return this.applyTo(v, amount)
  }

  private applyTo(v: Vehicle, amount: number): { hit: boolean; destroyed: boolean } {
    const wx = this.x + v.originX * this.SCALE
    if (v.turretAlive) {
      v.turretHp -= amount
      if (v.turretHp <= 0) {
        v.turretAlive = false
        if (v.turretNode) v.turretNode.visible = false
        this.onEvent?.('part', wx, this.y)
        return { hit: true, destroyed: true }
      }
      return { hit: true, destroyed: false }
    }

    v.hullHp -= amount
    if (v.hullHp <= 0) {
      v.hullAlive = false
      if (v.hullNode) v.hullNode.visible = false
      const survivor = this.vehicles.find((o) => o.hullAlive)
      if (survivor) this.onEvent?.('enrage', this.x + survivor.originX * this.SCALE, this.y)
      else {
        this.defeated = true
        this.deathTimer = 0
        this.onEvent?.('death', this.x, this.y)
      }
      return { hit: true, destroyed: true }
    }
    return { hit: true, destroyed: false }
  }

  reset(): void {
    this.active = false
    this.defeated = false
    this.age = 0
    this.deathTimer = -1
    this.group.visible = false
    if (this.model) {
      this.model.rotation.set(Math.PI / 2, 0, 0)
      this.model.scale.setScalar(this.SCALE)
    }
    for (const v of this.vehicles) {
      v.hullHp = 1600
      v.turretHp = 1100
      v.hullAlive = true
      v.turretAlive = true
      if (v.hullNode) v.hullNode.visible = true
      if (v.turretNode) v.turretNode.visible = true
    }
  }

  dispose(): void {
    this.model?.traverse((o) => {
      const m = o as THREE.Mesh
      if (m.isMesh) {
        m.geometry.dispose()
        for (const mat of Array.isArray(m.material) ? m.material : [m.material]) mat.dispose()
      }
    })
  }
}
